<template>
  <div ref="root" class="text-black/40">
    <p v-for="(chars, pi) in parsed" :key="pi" style="white-space: pre-wrap">
      <span v-for="(char, ci) in chars" :key="ci" data-reveal-char>{{
        char
      }}</span>
    </p>
  </div>
</template>

<script setup lang="ts">
interface Props {
  paragraphs: string[]
}

const props = defineProps<Props>()

// Split each paragraph into individual characters (spaces included) so each can
// be coloured independently. `white-space: pre-wrap` on the paragraph preserves
// the space characters while still wrapping at them, so words never break.
const parsed = computed(() =>
  props.paragraphs.map((paragraph) => [...paragraph]),
)

const root = ref<HTMLElement>()
let chars: HTMLElement[] = []
let frame = 0

// Grey → black. The reveal front sweeps through the characters as the block
// scrolls through the viewport; `BAND` is how many characters are mid-way
// through the transition at once (a soft edge rather than a hard wavefront).
const START_ALPHA = 0.4
const BAND = 12

const paint = () => {
  const el = root.value
  if (!el || !chars.length) return

  const vh = window.innerHeight
  const rect = el.getBoundingClientRect()

  // 0 when the top edge hits 85% of the viewport, 1 when the bottom edge
  // reaches 20% of the viewport — mirrors a scrub ScrollTrigger.
  const distance = 0.85 * vh - rect.top
  const total = 0.65 * vh + rect.height
  const progress = Math.min(1, Math.max(0, distance / total))

  const front = progress * (chars.length + BAND)
  for (let i = 0; i < chars.length; i++) {
    const local = Math.min(1, Math.max(0, (front - i) / BAND))
    const alpha = START_ALPHA + (1 - START_ALPHA) * local
    chars[i]!.style.color = `rgba(0, 0, 0, ${alpha})`
  }
}

const onScroll = () => {
  if (frame) return
  frame = requestAnimationFrame(() => {
    frame = 0
    paint()
  })
}

onMounted(() => {
  chars = Array.from(
    root.value?.querySelectorAll<HTMLElement>('[data-reveal-char]') ?? [],
  )

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    chars.forEach((char) => (char.style.color = 'rgba(0, 0, 0, 1)'))
    return
  }

  paint()
})

onBeforeUnmount(() => {
  if (frame) cancelAnimationFrame(frame)
})

useEventListener(window, 'scroll', onScroll, { passive: true })
useEventListener(window, 'resize', onScroll, { passive: true })
</script>
